import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard, { type ArticleItem } from "@/components/base/ArticleCard";
import HotRanking from "@/pages/home/components/HotRanking";
import FilterBar from "@/pages/daily/components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function Daily() {
  const [params] = useSearchParams();
  const catParam = params.get("cat") || "all";
  const qParam = params.get("q") || "";
  const [activeCat, setActiveCat] = useState(catParam);
  const [keyword, setKeyword] = useState(qParam);
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  useEffect(() => {
    setActiveCat(catParam);
  }, [catParam]);

  useEffect(() => {
    setKeyword(qParam);
  }, [qParam]);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const filtered = articles.filter((a) => {
      const matchCat = activeCat === "all" || a.category === activeCat;
      const matchKw =
        kw === "" ||
        a.title.toLowerCase().includes(kw) ||
        a.summary.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
    return [...filtered].sort((a, b) =>
      sort === "hot" ? b.hot - a.hot : b.date.localeCompare(a.date) || b.time.localeCompare(a.time),
    );
  }, [activeCat, keyword, sort]);

  usePageMeta({
    title: "每日日报列表｜黑料今日·每日更新栏目与爆料归档",
    description:
      "黑料今日每日日报列表页，按顶流追踪、影视剧透、综艺风波等栏目归档当日爆料，支持关键词搜索与最新、最热排序，方便回溯往期精选汇总。",
    keywords: "黑料今日日报,每日更新栏目,每日爆料,栏目归档,黑料今日列表",
    path: "/daily",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "黑料今日 · 每日日报列表",
        url: "https://043r0.cn/daily",
        description: "按栏目归档的每日爆料与精选汇总，支持搜索与排序。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://043r0.cn/" },
          { "@type": "ListItem", position: 2, name: "每日日报", item: "https://043r0.cn/daily" },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="px-4 md:px-8 pt-8 md:pt-10 pb-6 bg-background-100 border-b border-background-200">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "每日日报" }]} />
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">
            每日日报
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            编辑部每天把热门讨论整理成一篇篇短小的爆料条目，按栏目归档。可以用关键词搜索，
            也可以只看你关心的栏目，从最新或最热两个角度浏览。
          </p>
        </section>

        <section className="px-4 md:px-8 py-8 md:py-10">
          <FilterBar
            activeCat={activeCat}
            keyword={keyword}
            sort={sort}
            onCatChange={setActiveCat}
            onKeywordChange={setKeyword}
            onSortChange={setSort}
            resultCount={list.length}
          />

          <div className="mt-8 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {list.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  {list.map((a: ArticleItem) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl" />
                  </span>
                  <p className="mt-4 font-heading font-bold text-foreground-900">没有找到匹配的条目</p>
                  <p className="mt-2 text-sm text-foreground-600">
                    试试更换关键词，或把栏目切换回「全部栏目」。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      setActiveCat("all");
                    }}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选条件
                  </button>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-5">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading font-bold text-lg text-foreground-950">栏目索引</h2>
                  <ul className="mt-4 space-y-1">
                    {categories.map((cat) => (
                      <li key={cat.id}>
                        <button
                          type="button"
                          onClick={() => setActiveCat(cat.id)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                            activeCat === cat.id
                              ? "bg-primary-50 text-primary-700"
                              : "text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <i className={cat.icon} />
                            {cat.name}
                          </span>
                          <span className="text-xs text-foreground-500">{cat.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <HotRanking />
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}